<template>
    <article>
        <p>
            The most effective method for evaluating an observability tool is to introduce a failure intentionally into a
            fairly complex system, and then observe how quickly the tool detects the root cause.
        </p>


        <p>
            We've built Coroot based on the belief that having high-quality telemetry data enables us to automatically
            pinpoint the root causes for over 80% of outages with precision. But you don't have to take our word for it—put it to the test yourself!
        </p>

        <p>
            To simplify this task for you, we created a Helm chart that deploys:
        </p>

        <ul>
            <li><a href="https://opentelemetry.io/docs/demo/architecture/" target="_blank">OpenTelemetry Demo</a>: a web store application built as a set of microservices that communicate with each other over gRPC or HTTP.</li>
            <li><a href="https://coroot.com" target="_blank">Coroot</a>: an eBPF-based observability platform that automatically analyzes telemetry data such as metrics, logs, traces, and profiles to help you quickly identify the root causes of outages.</li>
            <li><a href="https://chaos-mesh.org/" target="_blank">Chaos Mesh</a>: a chaos engineering tool for Kubernetes that allows you to simulate various fault scenarios.</li>
        </ul>

        <h2>#1: Installation</h2>

        <p>
            Add Coroot's Helm charts repo
        </p>

        <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update coroot
        </pre>

        <p>
            As Chaos Mesh requires defining the socket and type of the underlying contained daemons, the installation
            command depends on the particular environment:
        </p>


        <v-tabs v-model="installation" class="mb-4" color="blue darken-2">
            <v-tab>Kubernetes (docker)</v-tab>
            <v-tab>Kubernetes (containerd)</v-tab>
            <v-tab>K3S</v-tab>
            <v-tab>Kubernetes (CRI-O)</v-tab>
        </v-tabs>

        <v-tabs-items v-model="installation">
            <v-tab-item transition="scale">
            <pre v-hl.bash>
helm upgrade --install demo coroot/otel-demo
            </pre>
            </v-tab-item>

            <v-tab-item transition="scale">
            <pre v-hl.bash>
helm upgrade --install demo \
  --set chaos-mesh.chaosDaemon.runtime=containerd \
  --set chaos-mesh.chaosDaemon.socketPath=/run/containerd/containerd.sock \
  coroot/otel-demo
            </pre>
            </v-tab-item>

            <v-tab-item transition="scale">
            <pre v-hl.bash>
helm upgrade --install demo \
  --set chaos-mesh.chaosDaemon.runtime=containerd \
  --set chaos-mesh.chaosDaemon.socketPath=/run/k3s/containerd/containerd.sock \
  coroot/otel-demo
            </pre>
            </v-tab-item>

            <v-tab-item transition="scale">
            <pre v-hl.bash>
helm upgrade --install demo \
  --set chaos-mesh.chaosDaemon.runtime=crio \
  --set chaos-mesh.chaosDaemon.socketPath=/var/run/crio/crio.sock \
  coroot/otel-demo
            </pre>
            </v-tab-item>
        </v-tabs-items>

        <v-alert color="blue" icon="mdi-alert-octagon-outline" class="mt-2" outlined text >
            Currently, Coroot doesn't support Docker-in-Docker environments like MiniKube due to eBPF limitations
        </v-alert>

        <h2>#2: Accessing Demo App, Coroot, and Chaos Mesh</h2>

        <p>
            The chart sets up fixed NodePort services, enabling access to applications from outside the cluster using the IP address of any cluster node
        </p>

        <ul>
            <li>Demo app: http://&lt;NODE_IP&gt;:30005</li>
            <li>Coroot: http://&lt;NODE_IP&gt;:30001</li>
            <li>Chaos Mesh Dashboard: http://&lt;NODE_IP&gt;:30003</li>
        </ul>

        <p>
            Here's what the OpenTelemetry Demo looks like in Coroot:
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/service_map.png" />
        </v-card>

        <p>So far, everything is going well in the cluster, no problems have occurred:</p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/health.png" />
        </v-card>

        <p>
            Most posts on testing observability tools stop at this step. However, we will take it a step further by
            introducing failures into the system and observing the system's behavior "in the middle of an outage".
        </p>

        <h2>#3: Introducing a network failure</h2>

        <p>
            Now, let's set up a chaos experiment in Chaos Mesh.
            We'll simulate a scenario where the <var>cart</var> service loses connectivity with the Redis database.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/net_partition.png" />
        </v-card>

        <p>You can create a chaos experiment using Chaos Mesh Dashboard at <var>http://&lt;YOUR_NODE_IP&gt;:30003</var></p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/chaos_mesh.png" />
        </v-card>

        <p>Here is the specification of my experiment:</p>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  namespace: default
  name: redis-net-partition
spec:
  selector:
    namespaces:
      - default
    labelSelectors:
      app.kubernetes.io/component: redis
  mode: all
  action: partition
  duration: 10m
  direction: to
  target:
    selector:
      namespaces:
        - default
      labelSelectors:
        app.kubernetes.io/component: cartservice
    mode: all
        </pre>

        <p>
            If we navigate to the Demo app (<var>http://&lt;YOUR_NODE_IP&gt;:30005</var>),
            we'll notice that we are no longer able to add an item to the cart.
            Now, let's examine how this failure appears in Coroot.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/failure1_health.png" />
        </v-card>

        <p>
            The Application Health Summary indicates that <var>cartservice</var> is experiencing network connectivity
            issues with one of its upstream services. By clicking on this issue, we can delve into the details.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/failure1_net_details.png" />
        </v-card>

        <p>
            As seen here, Coroot has pinpointed the specific Pods encountering the network connectivity issue.
        </p>

        <p>
            Additionally, it indicates that there are errors in the <var>frontend</var> service logs.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/failure1_log_patterns.png" />
        </v-card>

        <p>
            Thanks to Coroot's capability to extract repeated patterns from logs, we can quickly identify that there
            are three unique errors in the logs, all of which are related to the Redis database.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/failure1_log_details.png" />
        </v-card>

        <h2>Bonus track</h2>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/memory_leak_summary.png" />
        </v-card>

        <p>
            After gathering statistics over a longer period of time, Coroot detected that <var>emailservice</var>
            has a memory leak and is being killed by the OOM Killer every 1.5 hours.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/otel-demo/memory_leak_details.png" />
        </v-card>

        <h2>Conclusion</h2>

        <p>
            In this post, I've only shown one way things can go wrong, but Chaos Mesh lets you test for many more issues.
            If you try this with Coroot, please let us know what you find. Together, we can make Coroot better and help
            many engineering teams investigate outages faster!
        </p>

        <p>
            Try <router-link :to="{name: 'cloud'}">Coroot Cloud</router-link> now (14-day free trial is available) or follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to install Coroot Community Edition.
        </p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>


export default {
    components: {},
    data() {
        return {
            installation: null,
        };
    },
}
</script>

<style scoped>
</style>
